import { FOLDER_ROOT_PATH, type FolderPath } from '@lib/library/folderPath';
import type { ModalTab } from '@lib/media/modalTab';

export function parseSearchParam(query: string = window.location.search) {
  return new URLSearchParams(query);
}

/**
 * Safely encodes a URI, handling URLs that are already encoded.
 * Uses the URL constructor which preserves existing percent-encoding
 * while properly encoding raw characters (spaces, unicode).
 */
export function safeEncodeURI(url: string): string {
  try {
    return new URL(url).href;
  } catch {
    try {
      return new URL(encodeURI(url)).href;
    } catch {
      return encodeURI(url);
    }
  }
}

const MAX_SLUG_LENGTH = 24;
// MongoDB ObjectId is 24 hex characters
const OBJECT_ID_LENGTH = 24;

/**
 * Converts a string to a URL-friendly slug.
 * - Converts to lowercase
 * - Replaces spaces and special characters with dashes
 * - Collapses multiple dashes into one
 * - Removes leading/trailing dashes
 * - Truncates to 24 characters
 */
export function toSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-') // Replace any non-alphanumeric chars with dash
    .replace(/-+/g, '-') // Collapse multiple dashes into one
    .replace(/^-|-$/g, '') // Remove leading/trailing dashes
    .slice(0, MAX_SLUG_LENGTH);
}

/**
 * Extracts and validates an ObjectId from a URL parameter that may include a slug suffix.
 * E.g., "6970d1d754a6deff0cb71d0d-my-library-name" → "6970d1d754a6deff0cb71d0d"
 * Returns null if the extracted string is not a valid MongoDB ObjectId (24 hex characters).
 */
export function extractObjectId(rawId: string): string | null {
  const id = rawId.slice(0, OBJECT_ID_LENGTH);
  // Validate it's a valid ObjectId (24 hex characters)
  if (!/^[a-f0-9]{24}$/i.test(id)) {
    return null;
  }
  return id;
}

/**
 * Generates a library URL path with the ID and optional name slug.
 * Example: /library/6970d1d754a6deff0cb71d0d-my-library-name
 *
 * `folderPath` opens the library in one of its folders: `?folder=/Trips/Lisbon`, read back by
 * `LibraryPageWrapper`. The root is the plain link. Segments are encoded one by one so the
 * separators stay readable in the address bar.
 *
 * `highlightMediaId` scrolls to and highlights that media: `?mediaId=`. Without a `folderPath`,
 * the page opens the folder holding it (`FolderRouteSync`).
 */
export function getLibraryUrl(
  libraryId: string,
  libraryName?: string,
  options?: { folderPath?: FolderPath; highlightMediaId?: string },
): string {
  const slug = libraryName ? toSlug(libraryName) : '';
  const path = slug ? `/library/${libraryId}-${slug}` : `/library/${libraryId}`;
  const folderPath = options?.folderPath;
  const params = [
    folderPath &&
      folderPath !== FOLDER_ROOT_PATH &&
      `folder=${folderPath.split('/').map(encodeURIComponent).join('/')}`,
    options?.highlightMediaId && `mediaId=${encodeURIComponent(options.highlightMediaId)}`,
  ].filter(Boolean);
  return params.length > 0 ? `${path}?${params.join('&')}` : path;
}

/**
 * Generates a media URL path with the library ID, media ID, and optional slugs.
 * Example: /library/6970d1d754a6deff0cb71d0d-my-library/media/507f1f77bcf86cd799439011-video-title
 *
 * `t` is a playhead position in seconds, YouTube-style: it becomes `?t=90`, and the media
 * preview opens with the playhead there (see `parseMediaTime`). Zero and anything
 * non-finite are dropped rather than written as `t=0` — a timestamp pointing at the first
 * frame says nothing the plain link doesn't.
 */
export function getMediaUrl(
  libraryId: string,
  mediaId: string,
  options?: {
    libraryName?: string;
    mediaName?: string;
    t?: number;
    /** `?tab=` — the panel the modal opens on. Read back by `LibraryPageWrapper`. */
    tab?: ModalTab;
    /** `?comment=` — a comment to select once the media's comments load. Needs `tab: 'comments'`. */
    commentId?: string;
  },
): string {
  const librarySlug = options?.libraryName ? toSlug(options.libraryName) : '';
  const mediaSlug = options?.mediaName ? toSlug(options.mediaName) : '';

  const libraryPart = librarySlug ? `${libraryId}-${librarySlug}` : libraryId;
  const mediaPart = mediaSlug ? `${mediaId}-${mediaSlug}` : mediaId;

  const path = `/library/${libraryPart}/media/${mediaPart}`;
  // Always in this order, so two links to the same thing are the same string.
  const params = new URLSearchParams();
  const t = formatMediaTime(options?.t);
  if (t) params.set('t', t);
  if (options?.tab) params.set('tab', options.tab);
  if (options?.commentId) params.set('comment', options.commentId);
  const query = params.toString();
  return query ? `${path}?${query}` : path;
}

/**
 * The link to one comment: its media, opened on the Comments tab with it selected, at the moment
 * it points to.
 *
 * `t` is carried as well as the comment even though the comment names its own anchor. It is what
 * gets the player to the right place before the comments have loaded — and after the comment has
 * been deleted, when the link has nothing left to select but still points at the moment someone
 * wanted a colleague to see. One URL builder for both this and the media's own link, so the two
 * cannot drift into different shapes.
 */
export function getCommentUrl(
  libraryId: string,
  mediaId: string,
  comment: { id: string; startSeconds: number },
  names?: { libraryName?: string; mediaName?: string },
): string {
  return getMediaUrl(libraryId, mediaId, {
    ...names,
    t: comment.startSeconds,
    tab: 'comments',
    commentId: comment.id,
  });
}

/**
 * Puts an app path on the clipboard as a full URL. False when the browser refused — no
 * permission, an insecure context, a document without focus — for the caller to say so.
 *
 * One place for the origin and the refusal, shared by every "Copy link" in the app.
 */
export async function copyAppLink(path: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(`${window.location.origin}${path}`);
    return true;
  } catch {
    return false;
  }
}

/**
 * Seconds → the value we put in `?t=`. Whole seconds only: nothing in the UI can express
 * finer than that, and `t=90.3164` in a link the user is about to paste into Slack looks
 * like a bug.
 *
 * Returns `null` for anything not worth writing — zero, negative, NaN, Infinity — so callers
 * can treat "no timestamp" and "a timestamp of nothing" the same way.
 */
export function formatMediaTime(seconds: number | undefined | null): string | null {
  if (seconds === undefined || seconds === null || !Number.isFinite(seconds)) return null;
  const whole = Math.floor(seconds);
  return whole > 0 ? String(whole) : null;
}

/**
 * `?t=` → seconds.
 *
 * We only ever *write* plain seconds, but people hand-edit these and paste them between
 * players, so the accepted forms are the ones a human plausibly types: `90`, `90s`,
 * `1m30s`, `1h2m3s` (YouTube's shape), and `1:30` / `1:02:03` (the shape every player
 * displays). Bare seconds may carry a fraction; the result is floored either way.
 *
 * Returns `null` on anything unparseable, which callers must treat as "no timestamp" and
 * not as zero — a garbled `t` should leave the media where it would have opened anyway,
 * not silently rewind it.
 */
export function parseMediaTime(raw: string | null | undefined): number | null {
  if (!raw) return null;
  const value = raw.trim().toLowerCase();
  if (!value) return null;

  // 1:30 / 1:02:03 — minutes:seconds, optionally hours in front.
  if (value.includes(':')) {
    const parts = value.split(':');
    if (parts.length > 3) return null;
    let total = 0;
    for (const part of parts) {
      if (!/^\d+(\.\d+)?$/.test(part)) return null;
      total = total * 60 + Number(part);
    }
    return clampParsed(total);
  }

  // 90 / 90.5 — bare seconds.
  if (/^\d+(\.\d+)?$/.test(value)) return clampParsed(Number(value));

  // 1h2m3s, 1m30s, 90s — at least one unit, each at most once, in descending order.
  const units = value.match(/^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/);
  if (!units || (!units[1] && !units[2] && !units[3])) return null;
  const [, h, m, s] = units;
  return clampParsed(Number(h ?? 0) * 3600 + Number(m ?? 0) * 60 + Number(s ?? 0));
}

/** Shared tail of `parseMediaTime`: whole seconds, and zero means "no timestamp". */
function clampParsed(seconds: number): number | null {
  if (!Number.isFinite(seconds)) return null;
  const whole = Math.floor(seconds);
  return whole > 0 ? whole : null;
}
